/**
 * RowActions — dropdown unificado pras acoes de uma row de DataTable.
 *
 * Botao MoreVertical (3 pontos) que abre um popup com a lista de acoes.
 * Cada acao tem label, icon, onClick e variant (cor). Click fora fecha.
 *
 * Usado tanto no mobile (substitui multiplos botoes empilhados no card) quanto
 * no desktop (substitui colunas de Acoes amplas com varios icones).
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

export type RowActionVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger';

export type RowAction = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: RowActionVariant;
  disabled?: boolean;
};

const VARIANT_CLASSES: Record<RowActionVariant, string> = {
  default:  'text-white/75 hover:text-white hover:bg-white/[0.05]',
  primary:  'text-[#00e1d7] hover:text-[#00e1d7] hover:bg-[#00e1d7]/10',
  success:  'text-emerald-400 hover:text-emerald-400 hover:bg-emerald-500/10',
  warning:  'text-amber-400 hover:text-amber-400 hover:bg-amber-500/10',
  danger:   'text-red-400 hover:text-red-400 hover:bg-red-500/10',
};

export function RowActions({
  actions,
  align = 'right',
  triggerSize = 'sm',
}: {
  actions: RowAction[];
  /** Alinhamento do popup em relacao ao trigger */
  align?: 'left' | 'right';
  /** Tamanho do botao trigger */
  triggerSize?: 'sm' | 'md';
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Calcula posicao absoluta do popup (em viewport coords pra position:fixed).
  // Faz isso em useLayoutEffect pra evitar flash de posicao errada.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const recalc = () => {
      const trig = triggerRef.current;
      if (!trig) return;
      const r = trig.getBoundingClientRect();
      const POPUP_W = 200;
      const POPUP_H = Math.min(window.innerHeight - 16, 44 * actions.length + 16);
      let left = align === 'right' ? r.right - POPUP_W : r.left;
      // Evita sair da tela
      if (left < 8) left = 8;
      if (left + POPUP_W > window.innerWidth - 8) left = window.innerWidth - POPUP_W - 8;
      // Se nao cabe abaixo, abre acima
      let top = r.bottom + 4;
      if (top + POPUP_H > window.innerHeight - 8) top = r.top - POPUP_H - 4;
      if (top < 8) top = 8;
      setPos({ top, left });
    };
    recalc();
    window.addEventListener('scroll', recalc, true); // capture: pega scrolls de containers
    window.addEventListener('resize', recalc);
    return () => {
      window.removeEventListener('scroll', recalc, true);
      window.removeEventListener('resize', recalc);
    };
  }, [open, align, actions.length]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (popupRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (actions.length === 0) return null;

  const triggerClass = triggerSize === 'sm' ? 'w-7 h-7' : 'w-9 h-9';

  return (
    <>
      <button
        ref={triggerRef}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        className={`${triggerClass} rounded-md flex items-center justify-center
                    text-white/55 hover:text-white hover:bg-white/[0.06]
                    transition cursor-pointer`}
        title="Ações"
        aria-label="Ações da linha"
      >
        <MoreVertical size={triggerSize === 'sm' ? 14 : 16} />
      </button>

      {open && pos && createPortal(
        <div
          ref={popupRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: 200 }}
          className="z-[1000] py-1 rounded-lg
                     bg-[#0a1218]/98 backdrop-blur-xl border border-white/10
                     shadow-[0_8px_30px_rgba(0,0,0,0.5)]"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {actions.map((a, i) => (
            <button
              key={i}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (a.disabled) return;
                setOpen(false);
                a.onClick();
              }}
              disabled={a.disabled}
              className={`w-full px-3 py-2 flex items-center gap-2.5 text-xs font-semibold text-left transition cursor-pointer
                          disabled:opacity-40 disabled:cursor-not-allowed
                          ${VARIANT_CLASSES[a.variant ?? 'default']}`}
            >
              {a.icon && (
                <span className="w-4 flex items-center justify-center">{a.icon}</span>
              )}
              <span className="flex-1">{a.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
